import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { ChevronLeft, Eye, MoreHorizontal, Network } from 'lucide-react'
import { api } from '@/runtime'
import { useAllRuns, useNames, useResource, useSessionCards, useSettings } from '@/db/hooks'
import { Proposal } from '@/views/Chat'
import type { AgentRunResource } from '@/kinds/agentrun'
import { PRCard, SessionThread, isLead, repoName, useRunPhase } from '@/kinds/agentrun/components'
import { isOver } from '@/kinds/agentrun/schema'
import { AgentsLayout } from '@/components/AgentList'
import { AgentMenu } from '@/components/AgentMenu'
import { ArtifactButton, ArtifactDock, useArtifactsDocked } from '@/components/ArtifactPanel'
import { HolderDot, WaitingButton, useWaiting } from '@/components/Waiting'
import { RunTag } from '@/components/RunStatus'
import { HOLDER_WORD, waited } from '@/lib/waiting'
import { Empty, IconButton, SectionTitle, cx } from '@/components/ui'

// A coding agent's thread in the Agents hub (/agents/run/$id): the session's
// header, its pull request pinned on top, the conversation and the composer.

const Header = ({ run }: { run: AgentRunResource }) => {
  const phase = useRunPhase(run)
  const [menu, setMenu] = useState(false)
  const navigate = useNavigate()
  const working = phase === 'Working' || phase === 'Starting' || phase === 'Pending'
  return (
    // the top bar stays put while the thread scrolls under it: back, title, the
    // session's state and its menu always a thumb away. It reaches up behind the
    // status bar (the owner's call for this bar, 2026-10-03), so nothing of the
    // thread shows above it in the installed app
    <header
      className="glass-bar sticky top-0 z-20 -mx-3 px-3 pb-2 mb-3 bg-ink border-b border-line/60"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)',
        marginTop: 'calc(-1 * (env(safe-area-inset-top) + 0.5rem))',
      }}
      data-testid="run-topbar"
    >
      {/* the thread fades out under the bar, so a line half under it reads as scrolled, not as a sliver */}
      <span
        className="absolute inset-x-0 top-full h-5 bg-gradient-to-b from-ink to-transparent pointer-events-none"
        aria-hidden
      />
      <div className="flex items-center gap-1">
        {/* on a phone the way back is to every agent, not the browser's history */}
        <span className="lg:hidden -ml-2">
          <IconButton
            label="All agents"
            onClick={() => void navigate({ to: '/agents/all' })}
            data-testid="run-back"
          >
            <ChevronLeft size={20} aria-hidden />
          </IconButton>
        </span>
        <h1 className="flex-1 min-w-0 text-[17px] font-semibold tracking-tight leading-snug truncate">
          {isLead(run) ? 'Prime' : run.spec.title || run.spec.name}
        </h1>
        {isLead(run) && <WaitingButton />}
        {isLead(run) && <ArtifactButton run={run} />}
        {isLead(run) && (
          <IconButton
            label="Graph"
            onClick={() => void navigate({ to: '/agents/graph' })}
            data-testid="run-graph"
          >
            <Network size={20} aria-hidden />
          </IconButton>
        )}
        <IconButton label="More" onClick={() => setMenu(true)} data-testid="run-menu">
          <MoreHorizontal size={20} aria-hidden />
        </IconButton>
      </div>
      {/* rows that scroll sideways take the width they are given ([contain:inline-size]): their
          chips' full length otherwise widened the pane past the window, pushing the artefacts off it */}
      <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted overflow-x-auto whitespace-nowrap [contain:inline-size]">
        <RunTag run={run} phase={phase} />
        {isLead(run) ? (
          <>
            <span className="text-faint">Claude Code in the cluster</span>
            <LeadSpend />
          </>
        ) : (
          <>
            <span className="font-mono">{repoName(run.spec.repo_url)}</span>
            <WatchedTag run={run} />
          </>
        )}
        {run.spec.source_kind === 'task' && run.spec.source_ref && (
          <Link to="/tasks/$id" params={{ id: run.spec.source_ref }} className="text-accent-text underline">
            the task
          </Link>
        )}
      </div>
      {isLead(run) && <LeadAgents leadId={run.id} />}
      <AgentMenu run={run} open={menu} onClose={() => setMenu(false)} working={working} />
    </header>
  )
}

/**
 * The agents Prime looks after, as state in its top bar: a chip each saying
 * who holds its work now (lib/waiting.ts: you, Prime, or running), yours
 * first; tap to open. (Their start cards are not kept in the thread: this is
 * where they live.) An agent with nothing waiting on anyone is left out.
 */
const LeadAgents = ({ leadId }: { leadId: string }) => {
  const { items, now } = useWaiting()
  const list = items.filter((w) => w.run && w.run.id !== leadId)
  if (!list.length) return null
  return (
    <ul
      className="flex gap-1.5 overflow-x-auto mt-2 -mx-3 px-3 pb-0.5 [contain:inline-size]"
      data-testid="lead-agents"
    >
      {list.map((w) => {
        const r = w.run as AgentRunResource
        const state = w.holder === 'check' ? `Check ${waited(w.since, now)}` : HOLDER_WORD[w.holder]
        return (
          <li key={r.id} className="shrink-0" data-holder={w.holder}>
            <Link
              to="/agents/run/$id"
              params={{ id: r.id }}
              title={w.why}
              className="flex items-center gap-1.5 rounded-full border border-line bg-panel pl-2 pr-2.5 min-h-8 text-xs max-w-[15rem]"
            >
              <HolderDot holder={w.holder} />
              <span className="truncate">{r.spec.title || r.spec.name}</span>
              <span className={cx('shrink-0', w.holder === 'you' ? 'text-accent-text' : 'text-faint')}>
                {state}
              </span>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

/** "Prime is watching" on a coding agent's thread, when Prime runs and watches. */
const WatchedTag = ({ run }: { run: AgentRunResource }) => {
  const settings = useSettings()
  const lead = (useAllRuns() ?? []).some((r) => isLead(r) && !r.deleted_at && !isOver(r.spec.status))
  if (!lead || settings.prime_watches_agents === false || isOver(run.spec.status)) return null
  return (
    <span className="flex items-center gap-1 text-faint" data-testid="run-watched">
      <Eye size={12} aria-hidden /> Prime is watching
    </span>
  )
}

/** Today's spend by Prime and the agents, a way into the stats (online only). */
const LeadSpend = () => {
  const [usd, setUsd] = useState<number | null>(null)
  useEffect(() => {
    let stale = false
    api
      .agentStats(1)
      .then((s) => {
        if (!stale) setUsd(s.totals.usd)
      })
      .catch(() => {})
    return () => {
      stale = true
    }
  }, [])
  if (usd === null) return null
  return (
    <Link to="/agents/stats" className="text-accent-text underline" data-testid="lead-spend">
      ${usd.toFixed(2)} today
    </Link>
  )
}

/** Prime's cards (proposed over MCP), under its thread: Do it, or leave them. */
/**
 * Prime's waiting cards. `acts` overrides Settings agent_acts (the Now page
 * shows them and never acts on them by itself); `heading` titles the group.
 */
export const LeadCards = ({ runId, acts, heading }: { runId: string; acts?: boolean; heading?: string }) => {
  // a merge card whose pull request is no longer open has nothing left to do
  const runs = useAllRuns() ?? []
  const settled = (repo: string, n: number) =>
    runs.some(
      (r) =>
        r.spec.pr_number === n &&
        repoName(r.spec.repo_url) === repo &&
        (r.spec.pr_state === 'merged' || r.spec.pr_state === 'closed'),
    )
  const cards = useSessionCards(runId).filter(
    (s) =>
      !s.spec.ops.some((op) => {
        const a = (op.args ?? {}) as { repo?: string; number?: number }
        return op.type === 'merge_pr' && !!a.repo && !!a.number && settled(a.repo, a.number)
      }),
  )
  const { titleOf } = useNames()
  const settings = useSettings()
  if (!cards.length) return null
  return (
    <>
      {heading && <SectionTitle>{heading}</SectionTitle>}
      <div
        className={heading ? 'flex flex-col gap-2 mb-3' : 'flex flex-col gap-2 my-3'}
        data-testid="lead-cards"
      >
        {cards.map((s) => (
          <Proposal key={s.id} s={s} titleOf={titleOf} acts={acts ?? !!settings.agent_acts} />
        ))}
      </div>
    </>
  )
}

export const AgentRunView = () => {
  const { id } = useParams({ strict: false }) as { id: string }
  const run = useResource(id) as AgentRunResource | undefined
  const navigate = useNavigate()
  // words handed over for Prime's box (Ask Prime while it was not running, Search, a capture), unsent
  const { ask } = useSearch({ strict: false }) as { ask?: unknown }
  const lead = !!run && run.kind === 'AgentRun' && !run.deleted_at && isLead(run)
  const docked = useArtifactsDocked(lead ? run : undefined)
  return (
    <AgentsLayout currentId={id} docked={docked} side={lead && run ? <ArtifactDock run={run} /> : undefined}>
      {run === undefined ? null : !run || run.deleted_at || run.kind !== 'AgentRun' ? (
        <Empty>This agent is gone.</Empty>
      ) : (
        <SessionThread
          key={run.id}
          run={run}
          draft={ask == null ? undefined : String(ask)}
          cards={isLead(run) ? <LeadCards runId={run.id} /> : undefined}
          header={
            <>
              <Header run={run} />
              <PRCard
                run={run}
                onReview={
                  run.spec.pr_number
                    ? () => void navigate({ to: '/dev/review/$id', params: { id: run.id } })
                    : undefined
                }
              />
            </>
          }
        />
      )}
    </AgentsLayout>
  )
}
